import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { UiTextProvider } from "@/contexts/UiTextContext";
import { AppShell } from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MyProfile from "@/pages/MyProfile";
import Directory from "@/pages/Directory";
import UserManagement from "@/pages/UserManagement";
import ImportEmployees from "@/pages/ImportEmployees";
import WeekAheadDigest from "@/pages/WeekAheadDigest";
import CampusManagement from "@/pages/CampusManagement";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import AuditLog from "@/pages/AuditLog";
import LIFTHome from "@/pages/lift/LIFTHome";
import MyCyclePage from "@/pages/lift/MyCyclePage";
import SelfReflectionForm from "@/pages/lift/SelfReflectionForm";
import PeerRequestsPage from "@/pages/lift/PeerRequestsPage";
import PeerTasksInboxPage from "@/pages/lift/PeerTasksInboxPage";
import PeerResponseForm from "@/pages/lift/PeerResponseForm";
import ManagerHub from "@/pages/lift/ManagerHub";
import ManagerEvaluationForm from "@/pages/lift/ManagerEvaluationForm";
import SynthesisPage from "@/pages/lift/SynthesisPage";
import PacketPage from "@/pages/lift/PacketPage";
import CycleAdmin from "@/pages/lift/CycleAdmin";
import CycleDetail from "@/pages/lift/CycleDetail";
import RedactionQueue from "@/pages/lift/RedactionQueue";
import CompassLanding from "@/pages/compass/CompassLanding";
import CompassEntryForm from "@/pages/compass/CompassEntryForm";
import CompassPOQueue from "@/pages/compass/CompassPOQueue";
import CompassPlanBuilder from "@/pages/compass/CompassPlanBuilder";
import CompassPlanDetail from "@/pages/compass/CompassPlanDetail";
import CompassCalibration from "@/pages/compass/CompassCalibration";
import CompassSettings from "@/pages/compass/CompassSettings";
import ErLanding from "@/pages/er/ErLanding";
import ErIntakeForm from "@/pages/er/ErIntakeForm";
import ErMyReport from "@/pages/er/ErMyReport";
import ErCaseQueue from "@/pages/er/ErCaseQueue";
import ErCaseDetail from "@/pages/er/ErCaseDetail";
import ErResourceAdmin from "@/pages/er/ErResourceAdmin";
import ErNoticeEditor from "@/pages/er/ErNoticeEditor";
import ErAnonymousIntake from "@/pages/er/ErAnonymousIntake";
import ErAnonymousPortal from "@/pages/er/ErAnonymousPortal";
import TrainingLanding from "@/pages/training/TrainingLanding";
import TrainingTake from "@/pages/training/TrainingTake";
import TrainingTeamCompliance from "@/pages/training/TrainingTeamCompliance";
import TrainingContentAdmin from "@/pages/training/TrainingContentAdmin";
import TrainingContentEdit from "@/pages/training/TrainingContentEdit";
import TrainingAssign from "@/pages/training/TrainingAssign";
import TrainingCompliance from "@/pages/training/TrainingCompliance";
import ResourcesLanding from "@/pages/ResourcesLanding";
import ResourcesAdmin from "@/pages/ResourcesAdmin";
import VolunteerRegister from "@/pages/VolunteerRegister";
import VolunteerDashboard from "@/pages/VolunteerDashboard";
import VolunteerInvitesAdmin from "@/pages/VolunteerInvitesAdmin";
import RhythmLanding from "@/pages/rhythm/RhythmLanding";
import RhythmWorkspace from "@/pages/rhythm/RhythmWorkspace";
import MeetingRecord from "@/pages/rhythm/MeetingRecord";
import GrowthPlan from "@/pages/rhythm/GrowthPlan";
import RhythmDashboard from "@/pages/rhythm/RhythmDashboard";
import RhythmAdmin from "@/pages/rhythm/RhythmAdmin";
import WeeklyWinsSubmit from "@/pages/rhythm/WeeklyWinsSubmit";
import WeeklyWinsRollup from "@/pages/rhythm/WeeklyWinsRollup";
import WeeklyWinsReport from "@/pages/rhythm/WeeklyWinsReport";
import FaithJourney from "@/pages/FaithJourney";
import FaithJourneysRoster from "@/pages/FaithJourneysRoster";
import PraiseReport from "@/pages/PraiseReport";
import MyTeam from "@/pages/MyTeam";
import AckLink from "@/pages/AckLink";

const roleLevel = { volunteer: 0, employee: 1, manager: 2, po_admin: 3, super_admin: 4 };

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sage-200">
        <div className="patria-card p-8">
          <p className="font-serif text-2xl text-forest-900">Just a moment…</p>
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function RequireRole({ min, children }) {
  const { user } = useAuth();
  const level = roleLevel[user?.role || "employee"] ?? 1;
  if (level < roleLevel[min]) {
    return <Navigate to="/" replace />;
  }
  return children;
}

/** Redirect volunteers away from staff surfaces to their volunteer dashboard. */
function RequireStaff({ children }) {
  const { user } = useAuth();
  if (user?.role === "volunteer") {
    return <Navigate to="/volunteer" replace />;
  }
  return children;
}

/** Bounce the volunteer role to /volunteer on landing at "/". */
function DashboardOrVolunteer() {
  const { user } = useAuth();
  if (user?.role === "volunteer") return <Navigate to="/volunteer" replace />;
  return <Dashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Public ER anonymous routes — no auth required */}
      <Route path="/er/anonymous" element={<ErAnonymousIntake />} />
      <Route path="/er/anonymous/portal" element={<ErAnonymousPortal />} />
      {/* Public magic-link acknowledgment — no auth required */}
      <Route path="/ack/:token" element={<AckLink />} />
      <Route path="/volunteer/register" element={<VolunteerRegister />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardOrVolunteer />} />
        <Route path="volunteer" element={<VolunteerDashboard />} />
        <Route path="directory" element={<RequireStaff><Directory /></RequireStaff>} />
        <Route path="profile" element={<MyProfile />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="resources" element={<ResourcesLanding />} />
        <Route
          path="resources/admin"
          element={
            <RequireRole min="po_admin">
              <ResourcesAdmin />
            </RequireRole>
          }
        />
        <Route
          path="volunteer-invites"
          element={
            <RequireRole min="po_admin">
              <VolunteerInvitesAdmin />
            </RequireRole>
          }
        />
        {/* LIFT — staff only */}
        <Route path="lift" element={<RequireStaff><LIFTHome /></RequireStaff>} />
        <Route path="lift/cycle/:cycleId" element={<RequireStaff><MyCyclePage /></RequireStaff>} />
        <Route path="lift/self-reflection/:cycleId" element={<RequireStaff><SelfReflectionForm /></RequireStaff>} />
        <Route path="lift/peer-requests/:cycleId" element={<RequireStaff><PeerRequestsPage /></RequireStaff>} />
        <Route path="lift/peer-tasks" element={<RequireStaff><PeerTasksInboxPage /></RequireStaff>} />
        <Route path="lift/peer-tasks/:requestId" element={<RequireStaff><PeerResponseForm /></RequireStaff>} />
        <Route path="lift/manager/:cycleId" element={<RequireStaff><ManagerHub /></RequireStaff>} />
        <Route path="lift/manager/:cycleId/:employeeId" element={<RequireStaff><ManagerEvaluationForm /></RequireStaff>} />
        <Route path="lift/synthesis/:cycleId/:employeeId" element={<RequireStaff><SynthesisPage /></RequireStaff>} />
        <Route path="lift/packet/:cycleId/:employeeId" element={<RequireStaff><PacketPage /></RequireStaff>} />
        <Route
          path="lift/cycles"
          element={
            <RequireRole min="po_admin">
              <CycleAdmin />
            </RequireRole>
          }
        />
        <Route
          path="lift/cycles/:cycleId"
          element={
            <RequireRole min="po_admin">
              <CycleDetail />
            </RequireRole>
          }
        />
        <Route
          path="lift/redaction-queue"
          element={
            <RequireRole min="po_admin">
              <RedactionQueue />
            </RequireRole>
          }
        />
        {/* COMPASS — staff only */}
        <Route path="compass" element={<RequireStaff><CompassLanding /></RequireStaff>} />
        <Route
          path="compass/entry-request/new"
          element={
            <RequireRole min="manager">
              <CompassEntryForm />
            </RequireRole>
          }
        />
        <Route
          path="compass/entry-requests/:entryId/build-plan"
          element={
            <RequireRole min="manager">
              <CompassPlanBuilder />
            </RequireRole>
          }
        />
        <Route
          path="compass/po-queue"
          element={
            <RequireRole min="po_admin">
              <CompassPOQueue />
            </RequireRole>
          }
        />
        <Route path="compass/plans/:planId" element={<RequireStaff><CompassPlanDetail /></RequireStaff>} />
        <Route
          path="compass/calibration"
          element={
            <RequireRole min="po_admin">
              <CompassCalibration />
            </RequireRole>
          }
        />
        <Route
          path="compass/settings"
          element={
            <RequireRole min="super_admin">
              <CompassSettings />
            </RequireRole>
          }
        />
        {/* ER Center — identified surfaces staff only, anonymous surfaces public (above) */}
        <Route path="er" element={<RequireStaff><ErLanding /></RequireStaff>} />
        <Route path="er/intake" element={<RequireStaff><ErIntakeForm /></RequireStaff>} />
        <Route path="er/my-reports/:caseId" element={<RequireStaff><ErMyReport /></RequireStaff>} />
        <Route
          path="er/cases"
          element={
            <RequireRole min="po_admin">
              <ErCaseQueue />
            </RequireRole>
          }
        />
        <Route
          path="er/cases/:caseId"
          element={
            <RequireRole min="po_admin">
              <ErCaseDetail />
            </RequireRole>
          }
        />
        <Route
          path="er/resources/admin"
          element={
            <RequireRole min="po_admin">
              <ErResourceAdmin />
            </RequireRole>
          }
        />
        <Route
          path="er/notice/edit"
          element={
            <RequireRole min="super_admin">
              <ErNoticeEditor />
            </RequireRole>
          }
        />
        {/* Training */}
        <Route path="training" element={<TrainingLanding />} />
        <Route path="training/:assignmentId" element={<TrainingTake />} />
        <Route
          path="training/team"
          element={
            <RequireRole min="manager">
              <TrainingTeamCompliance />
            </RequireRole>
          }
        />
        <Route
          path="training/admin"
          element={
            <RequireRole min="po_admin">
              <TrainingContentAdmin />
            </RequireRole>
          }
        />
        <Route
          path="training/admin/:trainingId"
          element={
            <RequireRole min="po_admin">
              <TrainingContentEdit />
            </RequireRole>
          }
        />
        <Route
          path="training/admin/:trainingId/assign"
          element={
            <RequireRole min="po_admin">
              <TrainingAssign />
            </RequireRole>
          }
        />
        <Route
          path="training/reports"
          element={
            <RequireRole min="po_admin">
              <TrainingCompliance />
            </RequireRole>
          }
        />
        {/* RHYTHM — staff only. PO+ additional oversight surfaces. */}
        <Route path="rhythm" element={<RequireStaff><RhythmLanding /></RequireStaff>} />
        <Route path="team" element={<RequireStaff><MyTeam /></RequireStaff>} />
        <Route path="rhythm/workspace/:pairingId" element={<RequireStaff><RhythmWorkspace /></RequireStaff>} />
        <Route path="rhythm/meeting/:meetingId" element={<RequireStaff><MeetingRecord /></RequireStaff>} />
        <Route path="rhythm/growth/:employeeId" element={<RequireStaff><GrowthPlan /></RequireStaff>} />
        <Route
          path="rhythm/dashboard"
          element={<RequireStaff><RhythmDashboard /></RequireStaff>}
        />
        <Route
          path="rhythm/admin"
          element={
            <RequireRole min="po_admin">
              <RhythmAdmin />
            </RequireRole>
          }
        />
        <Route
          path="rhythm/weekly-wins"
          element={<RequireStaff><WeeklyWinsSubmit /></RequireStaff>}
        />
        <Route
          path="rhythm/weekly-wins/rollup"
          element={<RequireStaff><WeeklyWinsRollup /></RequireStaff>}
        />
        <Route
          path="rhythm/weekly-wins/reports/:reportId"
          element={<RequireStaff><WeeklyWinsReport /></RequireStaff>}
        />
        {/* Faith Journey — available to EVERYONE, including volunteers.
            Roster is PO+ only. */}
        <Route path="faith-journey" element={<FaithJourney />} />
        <Route
          path="faith-journeys"
          element={
            <RequireRole min="po_admin">
              <FaithJourneysRoster />
            </RequireRole>
          }
        />
        <Route
          path="praise-report"
          element={
            <RequireRole min="po_admin">
              <PraiseReport />
            </RequireRole>
          }
        />
        <Route
          path="people/manage"
          element={
            <RequireRole min="po_admin">
              <UserManagement />
            </RequireRole>
          }
        />
        <Route
          path="people/manage/import"
          element={
            <RequireRole min="po_admin">
              <ImportEmployees />
            </RequireRole>
          }
        />
        <Route
          path="week-ahead"
          element={
            <RequireRole min="po_admin">
              <WeekAheadDigest />
            </RequireRole>
          }
        />
        <Route
          path="campuses"
          element={
            <RequireRole min="super_admin">
              <CampusManagement />
            </RequireRole>
          }
        />
        <Route
          path="settings"
          element={
            <RequireRole min="super_admin">
              <Settings />
            </RequireRole>
          }
        />
        <Route
          path="audit-log"
          element={
            <RequireRole min="super_admin">
              <AuditLog />
            </RequireRole>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <UiTextProvider>
          <AppRoutes />
          <Toaster
            position="bottom-right"
            richColors
            toastOptions={{
              style: {
                background: "#FAF8F0",
                color: "#2C2C2C",
                border: "1px solid #C5C9BB",
              },
            }}
          />
          </UiTextProvider>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
